(function ($) {
  // ---------------------------------------------------------------------------
  // Consent gated video embeds.
  //
  // The facade (thumbnail + player shell) is always rendered because the page
  // HTML is cached before consent is known. The iframe only receives a real
  // `src` — and therefore only contacts the third party — once the visitor has
  // granted "marketing" consent AND clicked play.
  //
  // Three surfaces can occupy the facade box; they are mutually exclusive:
  //
  //   pre-consent   `.is-gated`    the consent layer with WPConsent's accept
  //                                button (rendered in the page HTML but hidden
  //                                by CSS until this file marks it, so a page
  //                                loaded by an already-consented visitor never
  //                                flashes the button)
  //   consented     `.is-ready`    the play disc
  //   playing       `.is-playing`  the loaded iframe
  //
  // Focus chain (keyboard):
  //
  //   accept button → Enter → media ("Play video")     [grant hands focus over]
  //   media         → Enter → iframe → Tab → the player's own controls
  //
  // Pointer activation never moves focus.
  //
  // Accept button / consent cookie: the plugin also has a delegated document
  // handler for `.wpconsent-iframe-accept-button` that saves only the single
  // category named on the button — `{essential, marketing}` here. That object
  // does not cover every entry in `wpconsent.slugs` (7 on this site), so on the
  // next pageview `processBannerDisplay()` reads it as outdated, expires the
  // cookie, brings the privacy banner back and leaves analytics locked even
  // though the visitor just accepted. So this file binds a listener at target
  // phase on OUR gate button only (inside `.c-video__consent`, never the
  // plugin's own placeholders) and:
  //
  //   feature-detect at click time — `window.WPConsent.savePreferences` plus an
  //   array `window.wpconsent.slugs`. Present → `stopPropagation()` first (the
  //   plugin listens at document, in bubble phase, so its partial save never
  //   runs: one write, not two), then hand `savePreferences()` a COMPLETE
  //   preferences object built by `buildStatsOnlyPrefs()`: `{essential: true}`
  //   plus every slug in `wpconsent.slugs` — `marketing` true, `marketing`
  //   false, and each service term resolved through `wpconsent.wpca_services`
  //   (true only when its category is `marketing`, otherwise false, unknown
  //   service → false) — with `marketing: true` guaranteed at the end, because
  //   marketing is the category this gate accepts.
  //
  //   Absent → do NOT stop propagation: the plugin's stock handler saves its
  //   partial object, which degrades to today's behaviour. This file never
  //   writes a consent cookie or calls `wp_set_consent` itself — every write
  //   stays inside the plugin's `savePreferences()` (cookie write, banner hide,
  //   script/iframe unlock, `wp_set_consent`, `wpconsent_consent_saved`), so
  //   the consent event chain that drives `evaluate()` below is unchanged.
  // ---------------------------------------------------------------------------
  var CONSENT_CATEGORY = "marketing";

  function hasConsent() {
    if (typeof window.wp_has_consent !== "function") {
      return false;
    }

    try {
      return !!window.wp_has_consent(CONSENT_CATEGORY);
    } catch (e) {
      return false;
    }
  }

  // Shapes the object the plugin's savePreferences() writes — it never writes
  // anything itself. The gate accepts "marketing", so the recorded choice has
  // to name every slug in wpconsent.slugs (the plugin expires a cookie whose
  // keys don't, and re-shows the banner on the next pageview). Service terms
  // are resolved via wpca_services because manual_toggle_services makes the
  // service key — not the category — decide whether that service unlocks, so
  // marketing services must be explicitly true for analytics to run later.
  function buildStatsOnlyPrefs() {
    var prefs = { essential: true };
    var slugs = window.wpconsent.slugs;
    var services = Array.isArray(window.wpconsent.wpca_services)
      ? window.wpconsent.wpca_services
      : [];

    slugs.forEach(function (slug) {
      if (slug === "essential") {
        return;
      }

      if (slug === CONSENT_CATEGORY) {
        prefs[slug] = true;
        return;
      }

      if (slug === "marketing") {
        prefs[slug] = false;
        return;
      }

      // Anything else is a service term: look up its category and only unlock
      // the services that belong to marketing (unknown service → false).
      var category = null;

      for (var i = 0; i < services.length; i++) {
        if (services[i] && services[i].slug === slug) {
          category = services[i].category;
          break;
        }
      }

      prefs[slug] = category === CONSENT_CATEGORY;
    });

    // The category this button accepts, whatever the slug list contained.
    prefs[CONSENT_CATEGORY] = true;

    return prefs;
  }

  // Target-phase listener on our own gate button. The plugin's partial-save
  // handler sits at document (bubble phase), so stopping propagation here
  // keeps it from running after us — no double write — while a missing API
  // falls through untouched to that stock handler. Detection runs at click
  // time, so a plugin script that loads after this bundle still counts.
  function bindGateAccept(button) {
    button.addEventListener("click", function (e) {
      var api = window.WPConsent;
      var config = window.wpconsent;

      if (
        !api ||
        typeof api.savePreferences !== "function" ||
        !config ||
        !Array.isArray(config.slugs)
      ) {
        // No API: let the plugin's own document handler run (partial save =
        // current behaviour). We never write a consent cookie ourselves.
        return;
      }

      e.stopPropagation();
      api.savePreferences(buildStatsOnlyPrefs());
    });
  }

  function videos() {
    return $(".c-video");
  }

  function getFrame(video) {
    return $(video).find(".c-video__frame")[0] || null;
  }

  // Page HTML is cached, so a page rendered before the facade became a <button>
  // may still carry a plain <div> as its play control. Mirror the button
  // semantics in place. Consent state itself is never touched here.
  function upgradeMedia(video) {
    var media = $(video).find(".c-video__media")[0];

    if (!media || media.tagName === "BUTTON") {
      return;
    }

    media.setAttribute("role", "button");
    media.setAttribute("tabindex", "0");
    media.setAttribute("aria-label", "Play video");
  }

  // Activating a control hides it (play swaps the facade for the player, a
  // grant swaps the consent layer for the play disc, a revoke swaps back),
  // which drops focus to <body> and loses the visitor's place: the next Tab
  // restarts at the top of the document. So after keyboard activation the
  // focus moves to whichever surface is visible now.
  //
  // Pointer activation is deliberately left alone — it only changes where Tab
  // would continue from, and moving focus after a click can flash a ring the
  // visitor never asked for. `:focus-visible` distinguishes the two for native
  // buttons and for the legacy <div> path alike (it is focused via Tab/Enter
  // there, so it reports keyboard too). Read it before the state swap: a hidden
  // element no longer reports :focus-visible.
  function wasKeyboard(el) {
    try {
      return el.matches(":focus-visible");
    } catch (e) {
      // No :focus-visible support: prefer the accessible behaviour. Moving
      // focus is harmless for pointer users (Tab position only).
      return true;
    }
  }

  function focusOrFail(el) {
    // focus() on a control that ended up display:none is a silent no-op, so a
    // race (e.g. consent revoked mid-click) degrades to today's behaviour.
    if (el && typeof el.focus === "function") {
      el.focus();
    }
  }

  function stop(video) {
    var frame = getFrame(video);

    if (frame) {
      // Removing the attribute (rather than blanking it) tears the player down
      // while `data-src` stays available for the next play.
      frame.removeAttribute("src");
    }

    $(video).removeClass("is-playing");
  }

  function withAutoplay(url) {
    if (!url || url.indexOf("autoplay=") !== -1) {
      return url;
    }

    // Facebook uses `&autoplay=true`, YouTube / Vimeo `autoplay=1`. The embed
    // URLs already carry a query string, so append with `&` when needed.
    var param =
      url.indexOf("facebook.com") !== -1 ? "autoplay=true" : "autoplay=1";
    return url + (url.indexOf("?") === -1 ? "?" : "&") + param;
  }

  function play(video) {
    var frame = getFrame(video);

    if (!frame) {
      return;
    }

    frame.setAttribute("src", withAutoplay(frame.getAttribute("data-src")));
    $(video).addClass("is-playing");
  }

  // Single source of truth for the two facade states. The consent decision is
  // never written into the cached HTML: `.is-gated` is added (or removed) from
  // here only, which is what keeps an already-consented page load free of the
  // accept button while a missing consent API — which reads as "no consent" —
  // still ends with the button on screen.
  function evaluate() {
    var granted = hasConsent();
    var active = document.activeElement;
    var refocus = null;

    // The consent layer is what holds focus when a grant lands (clicking or
    // keyboard-activating its accept button is how consent gets granted), and
    // hiding it would strand focus on <body>. Note it before the swap: a hidden
    // element can no longer tell us where it was.
    if (
      granted &&
      active &&
      active.closest &&
      active.closest(".c-video__consent")
    ) {
      refocus = active.closest(".c-video");
    }

    videos().toggleClass("is-gated", !granted);

    if (granted) {
      videos().addClass("is-ready");
    }

    if (refocus) {
      // The play disc has just appeared where the accept button was — the next
      // step of the same two-step flow.
      focusOrFail(refocus.querySelector(".c-video__media"));
    }
  }

  function revoke() {
    var active = document.activeElement;
    var refocus = null;

    // Keyboard play leaves focus inside the iframe; the revoke tears that
    // iframe down, so pick the surface to hand focus to before anything hides.
    videos().each(function () {
      var frame = getFrame(this);

      if (frame && active === frame && wasKeyboard(frame)) {
        refocus = this;
      }
    });

    videos()
      .removeClass("is-ready")
      .addClass("is-gated")
      .each(function () {
        stop(this);
      });

    if (refocus) {
      // The player is gone; the consent layer is the live surface again.
      focusOrFail(
        refocus.querySelector(".c-video__consent button") ||
          refocus.querySelector(".c-video__media"),
      );
    }
  }

  // Delegated so dynamically inserted sections keep working.
  document.addEventListener("click", function (e) {
    var target = e.target;
    if (!target || !target.closest) {
      return;
    }

    // The consent layer is a sibling of the media control, so its accept button
    // never resolves to `.c-video__media`: the plugin owns that click and this
    // handler stays out of its way (no second handler, no play).
    var media = target.closest(".c-video__media");
    if (!media) {
      return;
    }

    var video = media.closest(".c-video");
    var $video = $(video);

    // No play affordance before consent is granted.
    if (
      !video ||
      !$video.hasClass("is-ready") ||
      $video.hasClass("is-playing")
    ) {
      return;
    }

    var keyboardPlay = wasKeyboard(media);
    play(video);

    if (keyboardPlay) {
      // The facade is display:none now, so focusing it would silently do
      // nothing and focus would fall to <body>. The player is the live
      // surface: an iframe is focusable as it stands (deliberately no
      // tabindex="-1", so it also stays in the tab order and the player
      // remains reachable after a pointer-initiated play). From here Tab
      // walks into the player's own controls.
      focusOrFail(getFrame(video));
    }
  });

  // Enter/Space for facade markup that predates the <button> element. Native
  // buttons are skipped so the browser's own activation is never doubled.
  document.addEventListener("keydown", function (e) {
    if (e.key !== "Enter" && e.key !== " " && e.key !== "Spacebar") {
      return;
    }

    var media =
      e.target && e.target.closest ? e.target.closest(".c-video__media") : null;
    if (!media || media.tagName === "BUTTON") {
      return;
    }

    e.preventDefault();
    media.click();
  });

  document.addEventListener("wp_listen_for_consent_change", function (e) {
    if (!e.detail || !e.detail[CONSENT_CATEGORY]) {
      return;
    }

    if (e.detail[CONSENT_CATEGORY] === "allow") {
      evaluate();
    } else if (e.detail[CONSENT_CATEGORY] === "deny") {
      revoke();
    }
  });

  document.addEventListener("wp_consent_type_defined", function () {
    evaluate();
  });

  $(() => {
    videos().each(function () {
      upgradeMedia(this);

      // Our gate's accept button only — scoped by the selector, so the
      // plugin's placeholder buttons elsewhere on the page keep their own
      // handler untouched.
      var accept = this.querySelector(
        ".c-video__consent .wpconsent-iframe-accept-button",
      );

      if (accept) {
        bindGateAccept(accept);
      }
    });

    evaluate();

    // Safety net for a consent script that loads after the app bundle.
    if (typeof window.wp_has_consent !== "function") {
      setTimeout(evaluate, 2000);
    }
  });
})(jQuery);
